// src/pages/legal/PrivacyPolicy.js
//
// Routed at /privacy-policy, which the footer has linked to since it was
// written — that route did not exist, so the link 404'd.
//
// NOT LEGAL ADVICE. This is a working draft covering what your app actually
// does, based on the code: Firebase Auth, Firestore, Firebase Storage, Stripe,
// and Gmail-based transactional email. The sections that need counsel are
// flagged inline — children's data is the big one, because you collect it
// through schools and that is regulated activity, not a paragraph.

import React from "react";
import { Link } from "react-router-dom";
import { PRIVACY_VERSION, TERMS_EFFECTIVE_DATE, COMPANY } from "../../config/terms";

export default function PrivacyPolicy() {
  return (
    <div style={s.page}>
      <article style={s.sheet}>
        <header style={s.header}>
          <h1 style={s.h1}>Privacy Policy</h1>
          <p style={s.meta}>
            Effective {TERMS_EFFECTIVE_DATE} · Version {PRIVACY_VERSION}
          </p>
          <p style={s.lede}>
            This policy explains what {COMPANY.legalName} collects, why, and
            what choices you have. It forms part of our{" "}
            <Link to="/terms-of-service" style={s.link}>
              Terms &amp; Conditions
            </Link>
            .
          </p>
        </header>

        <Section n="1" title="What we collect">
          <p>
            <strong>Account information.</strong> Name, title, email address,
            state, grade, school name where given, the role you sign up as, and
            the educator code linking a student to a classroom.
          </p>
          <p>
            <strong>Usage and progress.</strong> Which topics were opened, how
            many times a video or song was played, quiz and bonus scores,
            question-by-question answers, avatar selection, and sign-in
            timestamps.
          </p>
          <p>
            <strong>Content you submit.</strong> Quiz questions, uploads, and
            anything typed into free-text fields.
          </p>
          <p>
            <strong>Payment information.</strong> Handled by Stripe. We receive
            a customer identifier and subscription status. We never see or
            store full card numbers.
          </p>
          <p>
            <strong>Technical information.</strong> Standard log data such as
            browser type and approximate region, used to keep the service
            running and secure.
          </p>
        </Section>

        <Section n="2" title="Children's information">
          <p>
            Student accounts are created and managed by a parent, guardian,
            educator, or school. Where a school or educator uses our service
            with students, we act as a service provider: the school directs
            what is collected and remains responsible for obtaining any consent
            required by law.
          </p>
          <p>
            We collect only what the product needs to work — a name, a
            classroom link, and progress. We do not require a home address,
            phone number, or date of birth for a student account. We do not
            show behavioural advertising to children, and we do not sell
            personal information.
          </p>
          <p>
            Please do not enter student names or identifying details into quiz
            questions, uploads, or other free-text fields, which may be
            reviewed by us or seen by other users.
          </p>
          <LegalFlag>
            COPPA, FERPA, and a growing set of state student-privacy statutes
            apply to this section, several of which require specific contract
            terms with schools and specific parental-consent mechanics. Have
            counsel review this before you sign a district.
          </LegalFlag>
        </Section>

        <Section n="3" title="How we use it">
          <ul style={s.list}>
            <li>To provide the service and keep you signed in.</li>
            <li>
              To show educators and parents the progress of students linked to
              their code.
            </li>
            <li>To process subscriptions and seats.</li>
            <li>
              To send account, billing, and service messages — including trial
              reminders and renewal notices.
            </li>
            <li>To improve our content and fix problems.</li>
            <li>To keep the service secure and to comply with the law.</li>
          </ul>
          <p>
            Where you submit quiz questions, we may review, edit, and include
            them in our content library as described in Section 8 of the Terms.
          </p>
        </Section>

        <Section n="4" title="Who we share it with">
          <p>
            <strong>Educators and parents</strong> see the progress of students
            who joined their classroom using their educator code.
          </p>
          <p>
            <strong>Service providers</strong> who run parts of the platform on
            our behalf: Google Firebase (authentication, database, file
            storage, hosting) and Stripe (payments). They may process data only
            on our instructions.
          </p>
          <p>
            We do not sell personal information. We may disclose information if
            required by law or to protect the safety of a child or the
            integrity of the service. If we are acquired, information may
            transfer as part of that transaction.
          </p>
        </Section>

        <Section n="5" title="Retention">
          <p>
            We keep account and progress information for as long as the account
            is active, and for a reasonable period afterwards for backups,
            billing records, and dispute resolution. Schools and educators may
            ask us to delete student records at any time.
          </p>
        </Section>

        <Section n="6" title="Your choices">
          <p>
            You can view and update most account details from your dashboard.
            To request a copy of your information, correct it, or delete it,
            contact us at{" "}
            <a href={`mailto:${COMPANY.email}`} style={s.link}>
              {COMPANY.email}
            </a>{" "}
            or{" "}
            <a href={`tel:${COMPANY.phoneHref}`} style={s.link}>
              {COMPANY.phone}
            </a>
            . A parent or guardian may request deletion of their child's
            information; where a school created the account, we will direct the
            request to the school.
          </p>
          <p>
            Deleting an account removes access and progress data. Some records
            are retained where we are required to keep them, such as billing
            records.
          </p>
        </Section>

        <Section n="7" title="Security">
          <p>
            We use industry-standard measures including encrypted transport,
            authenticated access, and database rules restricting who can read
            what. No system is perfectly secure, and we cannot guarantee
            absolute security. Tell us immediately if you believe an account
            has been compromised.
          </p>
        </Section>

        <Section n="8" title="Changes">
          <p>
            We may update this policy. Material changes will be reflected in
            the version and effective date above, and we will notify account
            holders where appropriate.
          </p>
        </Section>

        <Section n="9" title="Contact">
          <ul style={s.contactList}>
            <li>
              <strong>Mail:</strong> {COMPANY.address}
            </li>
            <li>
              <strong>Email:</strong>{" "}
              <a href={`mailto:${COMPANY.email}`} style={s.link}>
                {COMPANY.email}
              </a>
            </li>
            <li>
              <strong>Phone:</strong>{" "}
              <a href={`tel:${COMPANY.phoneHref}`} style={s.link}>
                {COMPANY.phone}
              </a>
            </li>
          </ul>
        </Section>

        <footer style={s.footer}>
          <p style={s.footerText}>
            © {new Date().getFullYear()} {COMPANY.legalName}. All rights
            reserved.
          </p>
        </footer>
      </article>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section style={s.section}>
      <h2 style={s.h2}>
        <span style={s.num}>{n}.</span> {title}
      </h2>
      <div style={s.body}>{children}</div>
    </section>
  );
}

function LegalFlag({ children }) {
  if (process.env.NODE_ENV === "production") return null;
  return (
    <div style={s.flag}>
      <strong style={s.flagLabel}>Review with counsel:</strong> {children}
    </div>
  );
}

const s = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "40px 20px 80px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  },
  sheet: {
    maxWidth: 780,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    padding: "48px 52px 40px",
    color: "#1e293b",
    lineHeight: 1.65,
  },
  header: { borderBottom: "1px solid #e2e8f0", paddingBottom: 24 },
  h1: { margin: 0, fontSize: 34, fontWeight: 800, color: "#0f172a" },
  meta: { margin: "8px 0 16px", color: "#64748b", fontSize: 13, fontWeight: 600 },
  lede: { margin: 0, fontSize: 16, color: "#334155" },
  section: { marginTop: 34 },
  h2: { fontSize: 19, fontWeight: 700, color: "#0f172a", margin: "0 0 10px" },
  num: { color: "#1d4ed8" },
  body: { fontSize: 15.5, color: "#334155" },
  list: { paddingLeft: 22, margin: "10px 0" },
  contactList: { listStyle: "none", padding: 0, margin: "10px 0" },
  flag: {
    background: "#eff6ff",
    border: "1px dashed #93c5fd",
    borderRadius: 8,
    padding: "12px 16px",
    margin: "14px 0",
    fontSize: 13.5,
    color: "#1e40af",
  },
  flagLabel: { display: "block", marginBottom: 2 },
  link: { color: "#1d4ed8", fontWeight: 600 },
  footer: { marginTop: 44, paddingTop: 22, borderTop: "1px solid #e2e8f0" },
  footerText: { margin: "4px 0", fontSize: 14, color: "#64748b" },
};
